/* eslint-disable camelcase */
/* eslint-disable no-param-reassign */

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Loading from "react-fullscreen-loading";

// region Imports - Material-UI
import {
  Fab,
  makeStyles,
  Switch,
  TextField,
  withStyles
} from "@material-ui/core";
import { Check } from "@material-ui/icons/";
// endregion Imports - Material-UI
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { SettingsMessages, ToastMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Shared
import { Param } from "@shared/entities/reg_params.entity";
import { ParamsKeys, ParamsNames } from "@shared/constants/params-names.enum";
// endregion Imports - Shared
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Components
import Header from "@components/Header";
import NumberField from "@atoms/NumberField";
// endregion Imports - Components
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
import { useAuth } from "@hooks/useAuth";
// endregion Imports - Hooks
// region Imports - Store
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";
// endregion Imports - Store
// region Imports - Styles
import { Container, OptionContainer, SectionCard } from "./styles";
// endregion Imports - Styles

// region Child components

/** Switch using the FLEET brand color when checked. */
const BrandSwitch = withStyles({
  switchBase: {
    "&$checked": { color: "#002951" },
    "&$checked + $track": { backgroundColor: "#002951" }
  },
  checked: {},
  track: {}
})(Switch);

type OptionProps = {
  title: string;
  helper?: string;
};

const Option: React.FC<OptionProps> = ({ children, title, helper }) => (
  <OptionContainer>
    <div className="label">
      <span className="title">{title}</span>
      {helper && <span className="helper">{helper}</span>}
    </div>
    <div className="control">
      {children}
    </div>
  </OptionContainer>
);
// endregion Child components

const Settings: React.FC = () => {

  // region States
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useState<Param[]>([]);
  const [minutesForUndueStop, setMinutesForUndueStop] = useState(0);
  const [pumpTimelineEnabled, setPumpTimelineEnabled] = useState(false);
  const [waterPostDischargeEnabled, setWaterPostDischargeEnabled] = useState(false);
  const [blockDate, setBlockDate] = useState("");
  const [defaultConstructionRadius, setDefaultConstructionRadius] = useState(0);
  const [secondsForImproperDischarge, setSecondsForImproperDischarge] = useState(60);
  const [autoTravelMinStop, setAutoTravelMinStop] = useState(0);
  // endregion States
  // region Hooks
  const screen = useSelector(({ screen: state }) => state);
  const { addToast } = useToast();
  const { t } = useTranslation();
  const { user } = useAuth();
  // endregion Hooks
  // region Styles
  const useStyles = makeStyles((theme) => ({
    fab: {
      backgroundColor: "#002951",
      color: "#FFF",
      position: "fixed",
      bottom: screen.platform === ScreenPlatform.MOBILE ? theme.spacing(10) : theme.spacing(4),
      right: theme.spacing(4),
      zIndex: 1000,
      "&:hover": {
        backgroundColor: "#002951"
      }
    }
  }));

  const classes = useStyles();
  // endregion Styles

  useEffect(() => {
    const loadParams = async () => {
      try {

        // Load params
        setLoading(true);
        const { data } = await api.get("params/read");

        if (data.status === "success") {
          const loadedParams = data.result as Param[];

          setParams(loadedParams);

          loadedParams.forEach((param) => {

            let minutes;

            switch (param.name) {
            case ParamsNames.SECONDS_FOR_UNDUE_STOP:
              minutes = Math.floor(Number(param.key) / 60);

              setMinutesForUndueStop(minutes);
              break;
            case ParamsNames.PUMP_TIMELINE_ENABLED:
              setPumpTimelineEnabled(param.key === ParamsKeys.PUMP_TIMELINE_ENABLED_TRUE);
              break;
            case ParamsNames.BLOCK_DATE:
              setBlockDate(param.key ?? "");
              break;
            case ParamsNames.DEFAULT_CONSTRUCTION_RADIUS:
              setDefaultConstructionRadius(Number(param.key));
              break;
            case ParamsNames.SECONDS_FOR_IMPROPER_DISCHARGE:
              setSecondsForImproperDischarge(Number(param.key));
              break;
            case ParamsNames.AUTO_TRAVEL_CONSTRUCTION_MIN_STOP_MINUTES:
              setAutoTravelMinStop(Number(param.key));
              break;
            case ParamsNames.WATER_POST_DISCHARGE_NEXT_TRAVEL_ENABLED:
              setWaterPostDischargeEnabled(param.key === ParamsKeys.WATER_POST_DISCHARGE_NEXT_TRAVEL_ENABLED_TRUE);
              break;

            default:
              break;
            }
          });

        } else addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

      } catch (error) {

        if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
        else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

      } finally {
        setLoading(false);
      }
    };

    loadParams();
  }, [addToast, t]);

  /**
   * Update params
   * @param params Params to update
   */
  const HandleSave = useCallback(async () => {
    try {

      setSaving(true);

      const seconds_for_undue_stop = (minutesForUndueStop * 60).toString();

      // Get only if parameter changed
      const changedParams = params.map((param) => {
        let changed = false;

        switch (param.name) {
        case ParamsNames.SECONDS_FOR_UNDUE_STOP:
          if (param.key !== seconds_for_undue_stop) {
            param.key = seconds_for_undue_stop;
            changed = true;
          }
          break;
        case ParamsNames.PUMP_TIMELINE_ENABLED:
          if ((param.key === ParamsKeys.PUMP_TIMELINE_ENABLED_TRUE && !pumpTimelineEnabled)
              || (param.key === ParamsKeys.PUMP_TIMELINE_ENABLED_FALSE && pumpTimelineEnabled)) {

            // Update param
            param.key = pumpTimelineEnabled ? ParamsKeys.PUMP_TIMELINE_ENABLED_TRUE : ParamsKeys.PUMP_TIMELINE_ENABLED_FALSE;

            // Update local storage with new key value
            changed = true;
          }
          break;

        case ParamsNames.BLOCK_DATE:
          if (param.key !== blockDate) {
            param.key = blockDate === "" ? "1" : blockDate;
            changed = true;
          }
          break;

        case ParamsNames.DEFAULT_CONSTRUCTION_RADIUS:
          if (param.key !== defaultConstructionRadius.toString()) {
            param.key = defaultConstructionRadius.toString();
            changed = true;
          }
          break;
        case ParamsNames.SECONDS_FOR_IMPROPER_DISCHARGE:
          if (param.key !== secondsForImproperDischarge.toString()) {
            param.key = secondsForImproperDischarge.toString();
            changed = true;
          }
          break;

        case ParamsNames.AUTO_TRAVEL_CONSTRUCTION_MIN_STOP_MINUTES:
          if (param.key !== autoTravelMinStop.toString()) {
            param.key = autoTravelMinStop.toString();
            changed = true;
          }
          break;

        case ParamsNames.WATER_POST_DISCHARGE_NEXT_TRAVEL_ENABLED:
          if ((param.key === ParamsKeys.WATER_POST_DISCHARGE_NEXT_TRAVEL_ENABLED_TRUE && !waterPostDischargeEnabled)
              || (param.key === ParamsKeys.WATER_POST_DISCHARGE_NEXT_TRAVEL_ENABLED_FALSE && waterPostDischargeEnabled)) {

            param.key = waterPostDischargeEnabled
              ? ParamsKeys.WATER_POST_DISCHARGE_NEXT_TRAVEL_ENABLED_TRUE
              : ParamsKeys.WATER_POST_DISCHARGE_NEXT_TRAVEL_ENABLED_FALSE;
            changed = true;
          }
          break;

        default:
          break;
        }

        return changed ? { "name": param.name, "key": param.key } : undefined;
      }).filter((param) => param);

      // Update params
      if (changedParams && changedParams.length > 0) {
        const { data } = await api.patch("params/update", { "params": changedParams });

        if (data.status === "success") {
          addToast({ type: "success", title: t(ToastMessages.success), description: data.message });
        } else {
          addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
        }
      }
    } catch (error) {

      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

    } finally {
      setSaving(false);
    }

  }, [
    addToast, params, minutesForUndueStop, pumpTimelineEnabled, waterPostDischargeEnabled, blockDate,
    defaultConstructionRadius, autoTravelMinStop, secondsForImproperDischarge, t
  ]);

  const handleDateChange = async (event: React.ChangeEvent) => {
    const date = (event.target as HTMLInputElement).value.toString();

    setBlockDate(date);

  };

  return (
    <>
      <Loading loading={loading} />
      <Header title={t(SettingsMessages.settingsTitle)} />
      <Container className="page">

        <SectionCard>
          <div className="card-header">
            <span className="card-title">{t(SettingsMessages.parameters)}</span>
          </div>
          <div className="card-body">
            <Option
              title={t(SettingsMessages.timeForUndueStopOptionDescription)}
              helper={t(SettingsMessages.timeForUndueStopOptionHelper)}
            >
              <NumberField
                label={t(SettingsMessages.minutes)}
                number={minutesForUndueStop}
                setNumber={setMinutesForUndueStop}
                min={1}
                max={999}
              />
            </Option>
            <Option
              title={t(SettingsMessages.timeForImproperDischargeOptionDescription)}
              helper={t(SettingsMessages.timeForImproperDischargeOptionHelper)}
            >
              <NumberField
                label={t(SettingsMessages.seconds)}
                number={secondsForImproperDischarge}
                setNumber={setSecondsForImproperDischarge}
                min={1}
                max={999}
              />
            </Option>
            <Option
              title={t(SettingsMessages.defaultConstructionRadiusOptionDescription)}
            >
              <NumberField
                label={t(SettingsMessages.meters)}
                number={defaultConstructionRadius}
                setNumber={setDefaultConstructionRadius}
                min={1}
                max={9999}
              />
            </Option>
            <Option
              title={t(SettingsMessages.autoTravelMinStopMinutes)}
            >
              <NumberField
                label={t(SettingsMessages.minutes)}
                number={autoTravelMinStop}
                setNumber={setAutoTravelMinStop}
                min={1}
                max={999}
              />
            </Option>
            {user.super_admin && (
              <Option
                title={t(SettingsMessages.blockDate)}
                helper={t(SettingsMessages.blockDateHelper)}
              >
                <TextField
                  className="dateInput"
                  name="date"
                  type="date"
                  value={blockDate}
                  InputLabelProps={{ shrink: true }}
                  onChange={handleDateChange}
                />
              </Option>
            )}
          </div>
        </SectionCard>

        {user.super_admin && (
          <SectionCard>
            <div className="card-header">
              <span className="card-title">{t(SettingsMessages.functionalities)}</span>
              <span className="card-chip">Super Admin</span>
            </div>
            <div className="card-body">
              <Option
                title={t(SettingsMessages.enablePumpTimelineOptionDescription)}
                helper={t(SettingsMessages.enablePumpTimelineOptionHelper)}
              >
                <BrandSwitch
                  checked={pumpTimelineEnabled}
                  onChange={() => setPumpTimelineEnabled((state) => !state)}
                />
              </Option>
              <Option
                title={t(SettingsMessages.enableWaterPostDischargeOptionDescription)}
                helper={t(SettingsMessages.enableWaterPostDischargeOptionHelper)}
              >
                <BrandSwitch
                  checked={waterPostDischargeEnabled}
                  onChange={() => setWaterPostDischargeEnabled((state) => !state)}
                />
              </Option>
            </div>
          </SectionCard>
        )}

        <Fab
          className={classes.fab}
          aria-label="save"
          onClick={HandleSave}
          disabled={saving}
        >
          <Check />
        </Fab>
      </Container>
    </>
  );
};

export default Settings;
